import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import TableRefreshButton from '../../../components/TableRefreshButton/TableRefreshButton';
import PageHeader from '../../../components/PageHeader/PageHeader';
import B2BClientService from '../../../services/B2BClientService';
import PriceExceptionsManager from '../clients/PriceExceptionsManager';
import { formatMoney } from '../../../utils/currency';

const emptyProfile = {
  name: '',
  description: '',
  generalDiscount: '',
  minimumOrderAmount: '',
  catalogVisibilityMode: 'ALL_EXCEPT_HIDDEN',
  priceExceptions: [],
  productVisibility: []
};

const modeLabels = {
  ALL_EXCEPT_HIDDEN: 'Todos excepto ocultos',
  ONLY_SELECTED: 'Solo productos seleccionados'
};

const normalizeProfile = (profile = emptyProfile) => ({
  ...emptyProfile,
  ...profile,
  generalDiscount: profile.generalDiscount ?? '',
  minimumOrderAmount: profile.minimumOrderAmount ?? '',
  priceExceptions: profile.priceExceptions || [],
  productVisibility: profile.productVisibility || []
});

const B2BCommercialProfiles = () => {
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [form, setForm] = useState(emptyProfile);
  const [productToAdd, setProductToAdd] = useState(null);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const visibilityKind = form.catalogVisibilityMode === 'ONLY_SELECTED' ? 'VISIBLE' : 'HIDDEN';
  const visibilityTitle = form.catalogVisibilityMode === 'ONLY_SELECTED'
    ? 'Productos visibles'
    : 'Productos ocultos';
  const visibilityDescription = form.catalogVisibilityMode === 'ONLY_SELECTED'
    ? 'El cliente solo podrá pedir los productos de esta lista.'
    : 'El cliente podrá pedir todos los productos salvo los de esta lista.';

  const loadData = async () => {
    setLoading(true);
    try {
      const [profilesData, productsData] = await Promise.all([
        B2BClientService.getCommercialProfiles(),
        B2BClientService.getAvailableProducts()
      ]);
      setProfiles(Array.isArray(profilesData) ? profilesData : []);
      setProducts(Array.isArray(productsData?.products) ? productsData.products : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const selectedProductIds = useMemo(
    () => new Set((form.productVisibility || []).map(item => item.productId)),
    [form.productVisibility]
  );

  const availableProducts = useMemo(
    () => products.filter(product => !selectedProductIds.has(product.id)),
    [products, selectedProductIds]
  );

  const openNewDialog = () => {
    setEditingProfile(null);
    setForm(emptyProfile);
    setProductToAdd(null);
    setDialogOpen(true);
  };

  const openEditDialog = async (profile) => {
    setLoading(true);
    try {
      const fullProfile = await B2BClientService.getCommercialProfileById(profile.id);
      setEditingProfile(fullProfile);
      setForm(normalizeProfile(fullProfile));
      setProductToAdd(null);
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingProfile(null);
    setForm(emptyProfile);
    setProductToAdd(null);
  };

  const handleModeChange = (mode) => {
    setForm(prev => ({
      ...prev,
      catalogVisibilityMode: mode,
      productVisibility: []
    }));
    setProductToAdd(null);
  };

  const addProductVisibility = () => {
    if (!productToAdd) return;
    setForm(prev => ({
      ...prev,
      productVisibility: [
        ...(prev.productVisibility || []),
        {
          productId: productToAdd.id,
          productName: productToAdd.name,
          visibility: visibilityKind
        }
      ]
    }));
    setProductToAdd(null);
  };

  const removeProductVisibility = (productId) => {
    setForm(prev => ({
      ...prev,
      productVisibility: (prev.productVisibility || []).filter(item => item.productId !== productId)
    }));
  };

  const saveProfile = async () => {
    const generalDiscount = Number(form.generalDiscount || 0);
    if (!Number.isFinite(generalDiscount) || generalDiscount < 0) {
      alert('El descuento general debe ser un número mayor o igual a 0.');
      return;
    }

    const minimumOrderAmount = form.minimumOrderAmount === '' || form.minimumOrderAmount === null
      ? null
      : Number(form.minimumOrderAmount);
    if (minimumOrderAmount !== null && (!Number.isFinite(minimumOrderAmount) || minimumOrderAmount < 0)) {
      alert('El mínimo de compra debe ser un número mayor o igual a 0.');
      return;
    }

    const priceExceptions = [];
    for (const exception of form.priceExceptions || []) {
      const productId = Number(exception.productId);
      const specialPrice = Number(exception.specialPrice);
      if (!Number.isInteger(productId) || productId <= 0) {
        alert('Hay una excepción de precio con producto inválido.');
        return;
      }
      if (!Number.isFinite(specialPrice) || specialPrice < 0) {
        alert(`El precio especial de "${exception.productName || `Producto ${productId}`}" debe ser un número mayor o igual a 0.`);
        return;
      }
      priceExceptions.push({
        productId,
        productName: exception.productName || `Producto ${productId}`,
        specialPrice
      });
    }

    const payload = {
      name: form.name.trim(),
      description: form.description?.trim() || null,
      generalDiscount,
      minimumOrderAmount: minimumOrderAmount && minimumOrderAmount > 0 ? minimumOrderAmount : null,
      catalogVisibilityMode: form.catalogVisibilityMode,
      priceExceptions,
      productVisibility: (form.productVisibility || []).map(item => ({
        productId: Number(item.productId),
        visibility: item.visibility
      }))
    };

    if (!payload.name) return;

    try {
      setSaving(true);
      if (editingProfile?.id) {
        await B2BClientService.updateCommercialProfile(editingProfile.id, payload);
      } else {
        await B2BClientService.createCommercialProfile(payload);
      }

      closeDialog();
      loadData();
    } catch (error) {
      const backendMessage = error?.response?.data?.error_message || error?.response?.data?.message;
      alert(backendMessage || 'No se pudo guardar el perfil comercial.');
    } finally {
      setSaving(false);
    }
  };

  const deleteProfile = async (profile) => {
    if (!window.confirm(`¿Eliminar el perfil "${profile.name}"? Los clientes asignados quedarán sin perfil.`)) {
      return;
    }
    await B2BClientService.deleteCommercialProfile(profile.id);
    loadData();
  };

  const openActions = (event, profile) => {
    setActionsAnchorEl(event.currentTarget);
    setSelectedProfile(profile);
  };

  const closeActions = () => {
    setActionsAnchorEl(null);
    setSelectedProfile(null);
  };

  const runAction = (callback) => {
    if (selectedProfile && callback) callback(selectedProfile);
    closeActions();
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Perfil',
      flex: 1.4,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" fontWeight={600}>{params.row.name}</Typography>
          {params.row.description && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {params.row.description}
            </Typography>
          )}
        </Box>
      )
    },
    {
      field: 'generalDiscount',
      headerName: 'Descuento general',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => `${params.value || 0}%`
    },
    {
      field: 'catalogVisibilityMode',
      headerName: 'Catálogo',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => modeLabels[params.value] || params.value
    },
    {
      field: 'minimumOrderAmount',
      headerName: 'Compra mínima',
      flex: 0.9,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value && params.value > 0
        ? formatMoney(params.value)
        : 'Sin mínimo'
    },
    {
      field: 'assignedClientsCount',
      headerName: 'Clientes',
      flex: 0.6,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 0.5,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <IconButton onClick={(event) => openActions(event, params.row)} aria-label={t('actions')}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )
    }
  ];

  return (
    <Box p={3}>
      <PageHeader
        title="Perfiles comerciales"
        subtitle="Condiciones B2B reutilizables y catálogo por grupo de clientes"
      />

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          <TableRefreshButton loading={loading} onClick={loadData} />
          <Button variant="outlined" startIcon={getIcon('add')} onClick={openNewDialog}>
            Agregar perfil
          </Button>
        </Box>
        <DataGrid
          rows={profiles}
          columns={columns}
          loading={loading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          getRowId={(row) => row.id}
        />
      </Paper>

      <Menu anchorEl={actionsAnchorEl} open={Boolean(actionsAnchorEl)} onClose={closeActions}>
        <MenuItem onClick={() => runAction(openEditDialog)}>
          {getIcon('settings')}
          <Box component="span" sx={{ ml: 1.5 }}>Modificar</Box>
        </MenuItem>
        <MenuItem onClick={() => runAction(deleteProfile)}>
          {getIcon('trash')}
          <Box component="span" sx={{ ml: 1.5 }}>Eliminar</Box>
        </MenuItem>
      </Menu>

      <Dialog
        open={dialogOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          closeDialog();
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{editingProfile ? 'Modificar perfil comercial' : 'Nuevo perfil comercial'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 2, mt: 1 }}>
            <TextField
              label="Nombre"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Descuento general"
              type="number"
              value={form.generalDiscount}
              onChange={(e) => setForm(prev => ({ ...prev, generalDiscount: e.target.value }))}
              fullWidth
              InputProps={{ endAdornment: '%' }}
            />
            <TextField
              label="Mínimo de compra"
              type="number"
              value={form.minimumOrderAmount}
              onChange={(e) => setForm(prev => ({ ...prev, minimumOrderAmount: e.target.value }))}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              helperText="UYU. Vacío o 0: sin mínimo."
            />
            <FormControl fullWidth>
              <InputLabel>Catálogo</InputLabel>
              <Select
                value={form.catalogVisibilityMode}
                label="Catálogo"
                onChange={(e) => handleModeChange(e.target.value)}
              >
                <MenuItem value="ALL_EXCEPT_HIDDEN">Todos excepto ocultos</MenuItem>
                <MenuItem value="ONLY_SELECTED">Solo productos seleccionados</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Descripción"
              value={form.description || ''}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              sx={{ gridColumn: { md: '1 / -1' } }}
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>Excepciones de precio</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Opcional. Definí precios especiales para productos puntuales.
            </Typography>
            <PriceExceptionsManager
              exceptions={form.priceExceptions}
              onExceptionsChange={(exceptions) => setForm(prev => ({ ...prev, priceExceptions: exceptions }))}
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>{visibilityTitle}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {visibilityDescription}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 2, alignItems: 'stretch' }}>
              <Autocomplete
                options={availableProducts}
                value={productToAdd}
                onChange={(_, value) => setProductToAdd(value)}
                getOptionLabel={(option) => option?.name || ''}
                renderInput={(params) => <TextField {...params} label="Producto" />}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
              <Button variant="outlined" onClick={addProductVisibility} disabled={!productToAdd} sx={{ minHeight: 56 }}>
                Agregar
              </Button>
            </Box>
            {(form.productVisibility || []).length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                {form.catalogVisibilityMode === 'ONLY_SELECTED'
                  ? 'Todavía no hay productos visibles. Con este modo, el portal no mostrará productos hasta que agregues alguno.'
                  : 'No hay productos ocultos. El portal mostrará todo el catálogo B2B disponible.'}
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {form.productVisibility.map(item => (
                  <Chip
                    key={item.productId}
                    label={item.productName || `Producto ${item.productId}`}
                    onDelete={() => removeProductVisibility(item.productId)}
                  />
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>{t('cancel')}</Button>
          <Button variant="contained" onClick={saveProfile} disabled={!form.name.trim() || saving}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default B2BCommercialProfiles;
